import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { eventActions } from '../../../../../../store/modules/event';
import { asyncSimpleModal, setMultipartForm } from '../../../../../../lib/common';
import toastMsg from '../../../../../../lib/toastMsg';
import EventApi from '../../../../../../lib/api/event/EventApi';
import {
  DATE_FORMAT,
  NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT,
} from '../../../../../../constants/service/common/utilConstants';
import eventToastMsg from '../../../../../../constants/service/toastMsg/eventToastMsg';
import { ProductItem } from '../../../types';
import { setDateFormat } from '../../../../../../lib/moment';

interface UseExcelProps {
  value: ProductItem[];
  onChange?: (value: ProductItem[]) => void;
}
const useExcel = (props: UseExcelProps) => {
  const { value, onChange } = props;
  const [isExcelUploadSuccess, setIsExcelUploadSuccess] = useState(false);
  const dispatch = useDispatch();
  const excelUpload = useMutation({
    mutationFn: (data: { attachment: File[] }) => EventApi.setEventProductExcelListUpload(setMultipartForm(data, true)),
    onSuccess: async (result: any) => {
      if (result.msg) throw new Error(result.msg);
      if (!onChange) return;
      toastMsg.success(eventToastMsg.SET_EVENT_PRODUCT_EXCEL_UPLOAD_REQUEST_SUCCESS);
      setIsExcelUploadSuccess(true);

      const { triedCnt, totalCnt, productList } = result.data;
      onChange(
        productList.map((item: any) => ({
          id: item.productId,
          name: item.productName,
          originPrice: item.originPrice,
          salesPrice: item.salesPrice,
          discountPeriod:
            item.discountStartDate && item.discountEndDate
              ? `${setDateFormat(item.discountStartDate, DATE_FORMAT.FULL_DASH_FORMAT)} -\n ${setDateFormat(
                  item.discountEndDate,
                  DATE_FORMAT.FULL_DASH_FORMAT,
                )}`
              : '-',
          createDate: item.createDate,
          discountPrice: item.discountPrice,
          discountRate: item.discountRate,
          imageUrl: item.url,
          category: item.mainCategory,
          brandId: item.storeId,
          brandName: item.brandName,
        })),
      );

      await asyncSimpleModal({
        type: 'info',
        title: '기획전 상품 업로드',
        message: ` ${triedCnt}개의 상품 중에서 ${totalCnt}개의 상품이 성공적으로 등록되었습니다. `,
      });
    },
    onError: (error: Error) => {
      toastMsg.error(error.message || eventToastMsg.SET_EVENT_PRODUCT_EXCEL_UPLOAD_REQUEST_ERROR, error);
    },
  });

  /**
   * 선택된 상품 엑셀 다운로드
   */
  const downloadSelectedExcel = () => {
    if (!value.length) {
      message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
      return;
    }

    dispatch(
      eventActions.getSelectedEventItemExcelDownload({
        productIdList: value.map((item) => item.id),
      }),
    );
  };

  /**
   * 상품 업로드
   */
  const uploadExcel = (file: File) => {
    excelUpload.mutate({ attachment: [file] });
    setIsExcelUploadSuccess(false);
  };

  return {
    isExcelUploadSuccess,
    downloadSelectedExcel,
    uploadExcel,
  };
};

export default useExcel;
